import { mergeProps } from '@react-aria/utils'
import React from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import { EmptyState } from '../EmptyState'
import { FieldMetadata, useFieldMetadata } from '../FieldMetadata'
import { Flex } from '../Flex'
import { FormInputProps } from '../FormInput'
import { FormLeftAdornment, FormRightAdornment } from '../FormInput/Adornments'
import { BaseInput, BaseInputOutline } from '../FormInput/BaseInput'
import useForwardRef from '../hooks/useForwardRef'
import { Icon } from '../Icon'
import { Loading } from '../Loading'
import { Tag } from '../Tag'
import { Typography } from '../Typography'
import { getReactHookFormError } from '../utils/formUtils'
import { Button } from '../Button'
import {
  calculateDropdownPosition,
  getFilteredOptions,
  getSelectedOptions,
  handleSelect,
  isSelectable,
  onRemove,
  scrollIntoViewIfNeeded,
} from './utils'

const DropdownTrigger = styled.div`
  position: relative;
`

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`

const DropdownMenu = styled.div<React.HTMLAttributes<HTMLDivElement> & { isOpen: boolean }>`
  position: fixed;
  z-index: 1000;
  border-radius: var(--radius-sm, 8px);
  border: 1px solid var(--border, #e2e6ed);
  background: var(--surface-primary, #fff);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
  max-height: 275px;
  min-width: max-content;
  overflow-y: auto;
  scrollbar-width: thin;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  align-items: flex-start;
`

const DropdownItem = styled(Typography).attrs<{
  ref?: React.RefObject<HTMLDivElement>
}>((props) => ({
  ref: props.ref,
  role: 'button',
}))<{
  isSelected?: boolean
  isHighlighted?: boolean
  disabled?: boolean
  index?: number
  maxIndex?: number
}>`
  user-select: none;
  padding: 0px 20px;
  height: 48px;
  display: flex;
  min-height: 48px;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  ${({ isSelected, isHighlighted }) => {
    const selectedHighlight = isSelected || isHighlighted
    return css`
      font-weight: ${isSelected && isHighlighted ? 600 : 400};
      background: ${selectedHighlight ? 'var(--gray100)' : 'transparent'};
      ${isSelected && 'color: var(--brand-primary);'}
    `
  }}

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`

const DropdownButtonItem = styled(DropdownItem)`
  color: var(--brand-primary, #6262f5);
  font-weight: 600;
`

const DropdownDividerItem = styled.div`
  height: 1px;
  width: 100%;
  border-bottom: 1px solid var(--border, #e2e6ed);
`

const DropdownSectionItem = styled(DropdownItem)`
  background: transparent;
  cursor: default;
  padding-top: 4px;
  padding-bottom: 0px;
  ${({ index }) =>
    index === 0 && 'height: calc(48px + 15px); min-height: calc(48px + 15px);'}
`

const EmptyStateContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
`

const ClearButton = styled.button<{ disabled?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 15px;
  background: none;
  border: none;
  cursor: pointer;

  path {
    fill: var(--brand-primary);
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}
`

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 15px;
  background: none;
  border: none;
`

export const FormInputAdornmentButton = styled(Button)`
  padding: 0 15px;
`

export type DropdownOptionType = 'item' | 'divider' | 'button' | 'section'

export type DropdownOptionBase<T = string> = {
  key?: string
  onClick?: (e: React.MouseEvent) => void
  disabled?: boolean
  style?: React.CSSProperties
  value?: T
}

export type SelectableDropdownOption<T = string> = DropdownOptionBase<T> & {
  value: T
  searchKey?: string // Used for searching when label is not a string
  label: React.ReactNode | string
  type?: 'item' | 'button'
}

export type NonSelectableDropdownOption<T = string> = DropdownOptionBase<T> & {
  type: 'divider' | 'section'
  label?: React.ReactNode | string
}

export type DropdownOption<T = string> =
  | SelectableDropdownOption<T>
  | NonSelectableDropdownOption<T>

export type DropdownSelectBaseProps<T = string> = Omit<
  FormInputProps,
  'value' | 'onChange'
> & {
  key?: string
  options?: DropdownOption<T>[]
  placeholder?: string
  loading?: boolean
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  selectedLabelRenderer?: (option: DropdownOption<T>) => React.ReactNode
  open?: boolean // Used to control the dropdown open state
  setIsOpen?: (isOpen: boolean) => boolean | undefined // callback can control whether the dropdown is open
}

export type ActionType = 'create' | 'select' | 'remove'

export type DropdownSelectDefaultProps<T = string> =
  DropdownSelectBaseProps<T> & {
    value?: T
    onChange?: (value: T, option: DropdownOption<T>, action: ActionType) => void
    mode?: 'default'
    allowCreateNew?: boolean
  }

export type DropdownSelectMultiProps<T = string> =
  DropdownSelectBaseProps<T> & {
    value?: T[]
    onChange?: (
      value: T[],
      option: SelectableDropdownOption<T>,
      action: ActionType
    ) => void
    mode: 'multi'
    allowCreateNew?: boolean
  }

export type DropdownSelectProps<T = string> =
  | DropdownSelectDefaultProps<T>
  | DropdownSelectMultiProps<T>

const SelectedItemsContainer = styled.div<{
  mode: DropdownSelectProps['mode']
  size: DropdownSelectProps['size']
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  padding: ${({ mode }) => (mode === 'multi' ? '8px 16px' : '0px 16px')};
  flex-wrap: ${({ mode }) => (mode === 'multi' ? 'wrap' : 'nowrap')};
  gap: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${({ mode }) => (mode === 'multi' ? 'normal' : 'nowrap')};

  min-height: ${({ size }) =>
    size === 'small' ? '32px' : size === 'medium' ? '40px' : '50px'};
  height: max-content;
  width: 100%;
  max-width: 100%;
`

export const reactHookFormErrorsToHide = [
  'Invalid input',
  'Invalid',
  'String must contain at least 1 character(s)',
]

export type OnChangeMultiCallback<T> = (
  value: T[],
  option: DropdownOption<T>,
  action: ActionType
) => void

export type OnChangeDefaultCallback<T> = (
  value: T,
  option: DropdownOption<T>,
  action: ActionType
) => void

function DropdownSelect<T = string | undefined>(
  {
    className,
    style,
    errorMessage,
    leftAdornment,
    rightAdornment,
    adornmentStyle,
    onChange,
    inputStyle,
    open: controlledOpen,
    setIsOpen: setControlledOpen,
    options,
    value,
    label,
    required,
    description,
    tooltip,
    mode = 'default',
    allowCreateNew = false,
    selectedLabelRenderer,
    onAdornmentClick: onAdornmentClickCb,
    ...props
  }: DropdownSelectProps<T>,
  ref: React.Ref<HTMLInputElement>
) {
  errorMessage =
    errorMessage && reactHookFormErrorsToHide.includes(errorMessage)
      ? ' '
      : errorMessage

  const hasError = Boolean(getReactHookFormError(errorMessage))

  const triggerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const highlightedRef = React.useRef<HTMLDivElement>(null)
  const backspaceTimeout = React.useRef<NodeJS.Timeout | null>(null)

  const [isOpen, _setIsOpen] = React.useState<boolean>(controlledOpen ?? false)
  const setIsOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const nextOpen = typeof next === 'function' ? next(isOpen) : next
    const allowed = setControlledOpen?.(nextOpen) ?? undefined
    _setIsOpen(allowed ?? nextOpen)
  }
  const [searchValue, setSearchValue] = React.useState('')
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1)
  const [backspaceCooldown, setBackspaceCooldown] = React.useState(false)

  const { fieldProps, ...fieldMetadataProps } = useFieldMetadata({
    ...props,
    errorMessage: getReactHookFormError(errorMessage),
  })

  const inputRef = useForwardRef<HTMLInputElement>(ref)
  const selectedItemsRef = React.useRef<HTMLDivElement>(null)
  const [inputHeight, setInputHeight] = React.useState<number | undefined>(
    undefined
  )

  const [dropdownPosition, setDropdownPosition] =
    React.useState<React.CSSProperties>({})

  const handlePositionUpdate = React.useCallback(() => {
    requestAnimationFrame(() => {
      const newPosition = calculateDropdownPosition(triggerRef.current)
      setDropdownPosition(newPosition)
    })
  }, [])

  React.useEffect(() => {
    let resizeObserver: ResizeObserver | undefined
    if (selectedItemsRef.current) {
      resizeObserver = new ResizeObserver((entries) => {
        const height = entries[0].target.scrollHeight
        setInputHeight(height > 0 ? height : undefined)
      })
      resizeObserver.observe(selectedItemsRef.current)
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking inside the dropdown menu
      const dropdownMenu = document.querySelector('[data-dropdown-menu="true"]')
      if (dropdownMenu?.contains(event.target as Node)) {
        return
      }

      if (
        dropdownRef.current &&
        !dropdownRef.current?.contains?.(event.target as Node) &&
        !triggerRef?.current?.contains?.(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (backspaceTimeout.current) {
        clearTimeout(backspaceTimeout.current)
      }
    }
  }, [])

  React.useEffect(() => {
    if (value && searchValue) {
      setSearchValue('')
    }
  }, [value])

  React.useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      scrollIntoViewIfNeeded(highlightedRef.current, dropdownRef.current)
    }
  }, [highlightedIndex, isOpen])

  React.useEffect(() => {
    if (controlledOpen !== isOpen) {
      setIsOpen(controlledOpen ?? false)
    }
  }, [controlledOpen])

  React.useEffect(() => {
    if (isOpen) {
      const newPosition = calculateDropdownPosition(triggerRef.current)
      setDropdownPosition(newPosition)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) return

    window.addEventListener('scroll', handlePositionUpdate, { passive: true })
    window.addEventListener('resize', handlePositionUpdate)
    return () => {
      window.removeEventListener('scroll', handlePositionUpdate)
      window.removeEventListener('resize', handlePositionUpdate)
    }
  }, [isOpen, handlePositionUpdate])

  const formattedOptions: DropdownOption<T>[] = React.useMemo(() => {
    return (options?.map((option) => ({
      ...option,
      type: option.type ?? 'item',
    })) ?? []) as DropdownOption<T>[]
  }, [options])

  const selectedOptions: DropdownOption<T>[] = React.useMemo(
    () => getSelectedOptions(formattedOptions, mode, value),
    [formattedOptions, value, mode]
  )
  const filteredOptions: DropdownOption<T>[] = React.useMemo(
    () =>
      getFilteredOptions(
        formattedOptions,
        searchValue,
        allowCreateNew,
        highlightedIndex,
        setHighlightedIndex,
        mode,
        value as T | T[],
        onChange,
        setIsOpen,
        setSearchValue
      ),
    [
      formattedOptions,
      searchValue,
      allowCreateNew,
      highlightedIndex,
      setHighlightedIndex,
      mode,
      value,
      onChange,
      setIsOpen,
      setSearchValue,
    ]
  )

  const hasValue = React.useMemo(() => {
    switch (mode) {
      case 'default':
        return !!value
      case 'multi':
        return Array.isArray(value) && value.length > 0
    }
  }, [value, mode])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (props.disabled) return
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === 'ArrowDown') {
          setIsOpen(true)
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          // istanbul ignore next
          e.preventDefault()
          // Go down. If it's at the bottom, go to the top
          // Also do not allow it to highlight 'button' or 'divider' options
          setHighlightedIndex((prev) => {
            let nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0
            let nextOption = filteredOptions[nextIndex] as
              | DropdownOption<T>
              | undefined
            // Find the next item option
            while (
              !isSelectable(nextOption) &&
              nextIndex < filteredOptions.length - 1
            ) {
              nextIndex += 1
              nextOption = filteredOptions[nextIndex] as
                | DropdownOption<T>
                | undefined
            }
            return nextIndex
          })
          break
        case 'ArrowUp':
          // istanbul ignore next
          e.preventDefault()
          // Go up. If it's at the top, go to the bottom
          setHighlightedIndex((prev) => {
            let nextIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1
            let nextOption = filteredOptions[nextIndex] as
              | DropdownOption<T>
              | undefined
            // Find the next item option
            while (!isSelectable(nextOption) && nextIndex > 0) {
              nextIndex--
              nextOption = filteredOptions[nextIndex] as
                | DropdownOption<T>
                | undefined
            }
            return nextIndex
          })
          break
        case 'Backspace':
          if (searchValue || inputRef.current?.value) return
          if (backspaceCooldown) return
          setBackspaceCooldown(true)
          if (backspaceTimeout.current) {
            clearTimeout(backspaceTimeout.current)
          }
          backspaceTimeout.current = setTimeout(
            () => setBackspaceCooldown(false),
            175
          )
          e.preventDefault()
          // Get the last selectable option
          const selectableOption = selectedOptions?.filter(isSelectable)?.pop()
          if (selectableOption) {
            onRemove(
              selectableOption as SelectableDropdownOption<T>,
              mode,
              value as T | T[],
              onChange
            )
          }
          break
        case 'Enter':
          // istanbul ignore next
          e.preventDefault()

          // If there is only one option, select it
          const firstOption = filteredOptions?.[0]
          if (
            filteredOptions.length === 1 &&
            isSelectable(firstOption) &&
            !firstOption?.disabled
          ) {
            handleSelect(
              firstOption as SelectableDropdownOption<T>,
              e as any,
              setIsOpen,
              mode,
              value as T | T[],
              onChange,
              setSearchValue
            )
            break
          }

          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            const option = filteredOptions[highlightedIndex]
            if (isSelectable(option) && !option?.disabled) {
              handleSelect(
                option as SelectableDropdownOption<T>,
                e as any,
                setIsOpen,
                mode,
                value as T | T[],
                onChange,
                setSearchValue
              )
            }
          }
          break
        case 'Escape':
          setIsOpen(false)
          break
      }
    },
    [
      props.disabled,
      isOpen,
      filteredOptions,
      selectedOptions,
      mode,
      value,
      onChange,
      inputRef,
      backspaceCooldown,
      setBackspaceCooldown,
      setIsOpen,
      setSearchValue,
    ]
  )

  const onAdornmentClick = React.useCallback(() => {
    if (props.disabled) return
    const ev = new MouseEvent('click', { bubbles: true })
    if (onAdornmentClickCb) {
      onAdornmentClickCb(ev as any)
    }
    handleClick?.(ev as any)
    if (isOpen) {
      inputRef.current?.blur?.()
    } else {
      inputRef.current?.focus?.()
    }
  }, [inputRef, props.disabled, isOpen])

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (props.disabled) return
      setIsOpen((prev) => {
        const next = !prev
        if (!next) {
          e.preventDefault()
          ;(e.target as HTMLInputElement)?.blur?.()
        }
        return next
      })
    },
    [props.disabled, setIsOpen]
  )

  const renderOption = React.useCallback(
    (option: DropdownOption<T>, index: number) => {
      let Renderer: React.ElementType
      switch (option.type) {
        case 'divider':
          Renderer = DropdownDividerItem
          break
        case 'button':
          Renderer = DropdownButtonItem
          break
        case 'section':
          Renderer = DropdownSectionItem
          break
        default:
          Renderer = DropdownItem
          break
      }

      let isSelected = false
      if (option.value !== undefined) {
        isSelected = Array.isArray(value)
          ? (value ?? []).includes(option.value as T)
          : option.value === value
      }

      return (
        <Renderer
          className='aiq-dropdown-select-menu-item'
          aria-label={option.key === 'create-new' ? 'Create New' : undefined}
          data-testid={`dropdown-${option.type ?? 'item'}`}
          key={`${option.value}-${index}`}
          index={index}
          isSelected={isSelected}
          maxIndex={filteredOptions.length - 1}
          isHighlighted={index === highlightedIndex}
          onClick={(e: React.MouseEvent) => {
            if (option.key === 'create-new') {
              option.onClick?.(e)
              return
            }
            if (isSelectable(option)) {
              handleSelect(
                option as SelectableDropdownOption<T>,
                e,
                setIsOpen,
                mode,
                value as T | T[],
                onChange,
                setSearchValue
              )
            }
          }}
          disabled={option?.disabled}
          style={option?.style}
          ref={index === highlightedIndex ? highlightedRef : undefined}
          onMouseEnter={() => setHighlightedIndex(index)}
        >
          {option.label}
        </Renderer>
      )
    },
    [
      highlightedIndex,
      value,
      mode,
      onChange,
      setIsOpen,
      setSearchValue,
      filteredOptions,
      isSelectable,
    ]
  )

  const dropdownPositionStyle: React.CSSProperties = React.useMemo(
    () => ({
      ...dropdownPosition,
      visibility: isOpen ? 'visible' : 'hidden',
    }),
    [dropdownPosition, isOpen]
  )

  return (
    <FieldMetadata
      {...fieldMetadataProps}
      required={required}
      label={label}
      description={description}
      tooltip={tooltip}
      className={className}
      style={style}
      baseOverrides={props.baseOverrides}
    >
      <DropdownContainer
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
        data-testid='dropdown-select-container'
      >
        <DropdownTrigger ref={triggerRef}>
          <BaseInputOutline
            ref={ref}
            type={props.type}
            hasError={hasError}
            {...props.baseOverrides}
          >
            {Boolean(leftAdornment) && (
              <FormLeftAdornment
                onClick={onAdornmentClick}
                disabled={props.disabled || props.loading}
              >
                {leftAdornment}
              </FormLeftAdornment>
            )}
            <BaseInput
              size={props.size}
              onClick={handleClick}
              onBlur={() =>{
                if (isOpen) return
                setSearchValue('')
              }}
              {...mergeProps(props, fieldProps)}
              style={{
                caretColor: hasValue ? 'transparent' : undefined,
                ...inputStyle,
                minHeight: inputHeight,
              }}
              value={searchValue}
              placeholder={hasValue ? undefined : props.placeholder}
              ref={inputRef}
              onChange={(e) => setSearchValue(e.target.value)}
              disabled={props.disabled || props.loading}
              hasLeftAdornment={Boolean(leftAdornment)}
              hasRightAdornment={
                (Boolean(rightAdornment) || props.clearable) ?? false
              }
              labelVariant={props.labelVariant}
              data-testid='form-input-testid'
              aria-label='form-input'
            />
            {!searchValue && (
              <SelectedItemsContainer
                ref={selectedItemsRef}
                mode={mode}
                size={props.size}
              >
                {selectedOptions?.map((opt, i) => (
                  <div
                    key={`selected-${opt.value}-${i}`}
                    style={{ maxWidth: 'calc(100% - 16px)' }}
                  >
                    {selectedLabelRenderer?.(opt) ?? (
                      <DropdownItemLabel
                        option={opt}
                        mode={mode}
                        size={props.size}
                        onRemove={(option) =>
                          onRemove(
                            option as SelectableDropdownOption<T>,
                            mode,
                            value as T | T[],
                            onChange
                          )
                        }
                      />
                    )}
                  </div>
                ))}
              </SelectedItemsContainer>
            )}

            {!(props.clearable && hasValue) && (
              <FormRightAdornment
                style={{ ...adornmentStyle, cursor: 'pointer' }}
                onClick={onAdornmentClick}
                disabled={props.disabled || props.loading}
              >
                {rightAdornment || (
                  <Icon
                    type={isOpen ? 'CHEVRON_UP' : 'CHEVRON_DOWN'}
                    height={20}
                    width={20}
                  />
                )}
              </FormRightAdornment>
            )}

            {props.clearable && hasValue && (
              <ClearButton
                disabled={props.disabled}
                onClick={(e) => {
                  if (typeof props.handleClear === 'function') {
                    props.handleClear(e)
                  } else {
                    e.preventDefault()

                    if (props.disabled) return

                    switch (mode) {
                      case 'default':
                        ;(onChange as OnChangeDefaultCallback<T>)?.(
                          undefined as T,
                          {} as DropdownOption<T>,
                          'remove'
                        )
                        break
                      case 'multi':
                        ;(onChange as OnChangeMultiCallback<T>)?.(
                          [],
                          {} as SelectableDropdownOption<T>,
                          'remove'
                        )
                        break
                    }
                  }
                }}
                aria-label='Clear Input'
              >
                <Icon
                  type='CLOSE_X'
                  height={20}
                  width={20}
                  css={css`
                    path {
                      fill: var(--black);
                    }
                  `}
                />
              </ClearButton>
            )}
            {props.loading && (
              <LoadingWrapper aria-label='Loading'>
                <Loading
                  css={css`
                    top: auto;
                    left: auto;
                  `}
                  size='small'
                />
              </LoadingWrapper>
            )}
          </BaseInputOutline>
        </DropdownTrigger>

        {isOpen &&
          !props.disabled &&
          createPortal(
            <DropdownMenu
              className='aiq-dropdown-select-menu'
              data-testid='aiq-dropdown-select-menu'
              data-dropdown-menu='true'
              isOpen={true}
              style={dropdownPositionStyle}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
              }}
            >
              {filteredOptions.map(renderOption)}

              {filteredOptions.length === 0 && (
                <EmptyStateContainer>
                  <EmptyState height='100px'>
                    <Typography variant='h4'>No options found</Typography>
                  </EmptyState>
                </EmptyStateContainer>
              )}
            </DropdownMenu>,
            document.body
          )}
      </DropdownContainer>
    </FieldMetadata>
  )
}

export default React.forwardRef(DropdownSelect) as <T = string | undefined>(props: DropdownSelectProps<T>) => JSX.Element

function _DropdownItemLabel<
  T = string,
>({
  option,
  mode,
  size,
  onRemove,
}: {
  option: DropdownOption<T>
  mode: DropdownSelectProps<T>['mode']
  size: DropdownSelectProps<T>['size']
  onRemove: (option: SelectableDropdownOption<T>) => void
}) {
  switch (mode) {
    case 'multi':
      return (
        <Tag
          variant='gray'
          data-testid='dropdown-item-label'
          size={size as 'small' | 'medium'}
          style={{
            position: 'relative',
            overflow: 'hidden',
            maxWidth: '100%',
            display: 'block',
            margin: 0,
            padding: 0,
            boxSizing: 'unset',
          }}
        >
          <Flex
            gap={4}
            padding='0px 4px'
            align='center'
            style={{
              height:
                size === 'small' ? '24px' : size === 'medium' ? '30px' : '36px',
              width: '100%',
            }}
          >
            <Flex
              flex
              align='center'
              style={{ maxWidth: 'calc(100% - 16px)', overflow: 'hidden' }}
            >
              {option.label}
            </Flex>
            <Flex
              flex
              center
              style={{
                background: 'var(--gray200)',
                minWidth: 'max-content',
                maxWidth: 'max-content',
                pointerEvents: 'auto',
                cursor: 'pointer',
                padding: size === 'large' ? 4 : 2,
                flexShrink: 0,
              }}
              onClick={() => onRemove(option as SelectableDropdownOption<T>)}
            >
              <Icon
                type='CLOSE_X'
                height={12}
                width={12}
                data-testid='dropdown-item-label-close-x'
              />
            </Flex>
          </Flex>
        </Tag>
      )
    default:
      return <>{option.label}</>
  }
}

export const DropdownItemLabel = React.memo(_DropdownItemLabel as any) as <T>(props: {
  option: DropdownOption<T>
  mode: DropdownSelectProps<T>['mode']
  size: DropdownSelectProps<T>['size']
  onRemove: (option: SelectableDropdownOption<T>) => void
}) => JSX.Element
